/**
 * Helpers for rendering and computing transaction amounts with direction.
 *
 * Amounts are always stored positive. `direction` ('expense' | 'income')
 * determines money flow. For cost/total math, income subtracts from expenses
 * via signed helpers below. UI always shows positive magnitudes; direction
 * is conveyed with color and arrows (see TransactionDirectionDisplay).
 */

export const TRANSACTION_INCOME = 'income';
export const TRANSACTION_EXPENSE = 'expense';

export function isIncomeTransaction(direction) {
    return direction === TRANSACTION_INCOME;
}

/** Signed numeric value: expense → +magnitude, income → -magnitude. */
export function signedTransactionAmount(amount, direction) {
    const magnitude = Math.abs(Number(amount) || 0);
    return isIncomeTransaction(direction) ? -magnitude : magnitude;
}

/** Sum signed allocation amounts; each row defaults to `defaultDirection`. */
export function signedAllocationsTotal(allocations, defaultDirection = TRANSACTION_EXPENSE) {
    if (!Array.isArray(allocations)) return 0;
    return allocations.reduce((sum, row) => {
        const magnitude = Math.abs(Number(row?.amount) || 0);
        const direction = row?.direction || defaultDirection;
        return sum + signedTransactionAmount(magnitude, direction);
    }, 0);
}

/** True when signed allocation net equals signed header amount (within one cent). */
export function allocationsBalanceHeader(amount, direction, allocations) {
    const headerSigned = signedTransactionAmount(amount, direction);
    const allocatedSigned = signedAllocationsTotal(allocations, direction);
    return Math.abs(allocatedSigned - headerSigned) < 0.01;
}

/**
 * Display a positive magnitude with optional currency formatter.
 * Direction affects styling only, not the numeric string.
 */
export function formatDisplayAmount(amount, direction, formatMagnitude) {
    const magnitude = Math.abs(Number(amount) || 0);
    if (typeof formatMagnitude === 'function') {
        return formatMagnitude(magnitude);
    }
    return `$${magnitude.toFixed(2)}`;
}

/** @deprecated Prefer formatDisplayAmount — app UI never prefixes minus signs. */
export function formatSignedAmount(amount, direction, formatMagnitude) {
    return formatDisplayAmount(amount, direction, formatMagnitude);
}

/** MUI sx for amount text: green + bold for income, default theme color for expense. */
export function transactionAmountSx(direction) {
    return isIncomeTransaction(direction)
        ? { color: 'success.main', fontWeight: 700 }
        : undefined;
}

/** Direction implied by a signed net (for allocation balance summaries). */
export function directionFromSignedValue(signedValue) {
    return Number(signedValue) < 0 ? TRANSACTION_INCOME : TRANSACTION_EXPENSE;
}
